import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AngelIcon from '@/components/base/AngelIcon';

const featureIcons = ['ri-lungs-line', 'ri-timer-flash-line', 'ri-body-scan-line', 'ri-pulse-line', 'ri-book-open-line'];

export default function FreeAngel() {
  const { t } = useTranslation();
  const [completedSessions, setCompletedSessions] = useState<number>(0);

  const features = [
    { key: 'f1', num: '①' },
    { key: 'f2', num: '②' },
    { key: 'f3', num: '③' },
    { key: 'f4', num: '④' },
    { key: 'f5', num: '⑤' },
  ];

  const sessions = [
    { key: 's1', step: 1 },
    { key: 's2', step: 2 },
    { key: 's3', step: 3 },
  ];

  const handleSessionComplete = (step: number) => {
    if (completedSessions < step) {
      setCompletedSessions(step);
    }
  };

  const allSessionsDone = completedSessions >= 3;

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />
      <main>
        {/* ── Hero ─────────────────────────────────────────── */}
        <section className="relative w-full min-h-[500px] md:min-h-[600px] flex flex-col items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20gentle%20golden%20light%20emerging%20through%20an%20open%20gate%20into%20a%20softly%20glowing%20horizon%2C%20ethereal%20morning%20atmosphere%2C%20minimalist%20spiritual%20art%2C%20abstract%20sacred%20geometry%2C%20no%20text%2C%20no%20people%2C%20soft%20gradients%2C%20warm%20off-white%20background%2C%20subtle%20radiant%20glow%2C%20peaceful%20dawn%20light%2C%20quiet%20and%20contemplative%20mood&width=1600&height=900&seq=free-angel-hero-01&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/30 via-background-50/10 to-background-50/70" />
          </div>

          <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center pt-24 pb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-foreground-400 mb-6">
              {t('freeAngel.title')} &middot; {t('freeAngel.titleEn')}
            </p>
            <h1
              className="text-3xl md:text-4xl lg:text-5xl leading-tight mb-4 text-foreground-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {t('freeAngel.title')}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 max-w-xl mx-auto mb-8">
              {t('freeAngel.subtitle')}
            </p>
            <div className="flex flex-col items-center gap-3 mb-8">
              <AngelIcon variant="gold" size="lg" />
              <p className="text-sm text-foreground-400 max-w-md leading-relaxed">
                {t('freeAngel.aiAngelNote')}
              </p>
            </div>
          </div>
        </section>

        {/* ── Five Permanent Free Features ────────────────── */}
        <section className="max-w-5xl mx-auto px-4 md:px-6 py-16 md:py-20">
          <h2
            className="text-2xl md:text-3xl text-center mb-4 text-foreground-900"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('freeAngel.features.title')}
          </h2>
          <p className="text-sm text-foreground-400 text-center mb-2">{t('freeAngel.features.titleEn')}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-10">
            {features.map((f, idx) => (
              <div
                key={f.key}
                className="bg-background-50 border border-foreground-200/60 rounded-lg p-5 md:p-6 flex flex-col gap-3 hover:border-accent-200/60 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl text-accent-500 font-medium shrink-0 w-8">{f.num}</span>
                  <div>
                    <div className="w-8 h-8 flex items-center justify-center text-accent-500 mb-1">
                      <i className={`${featureIcons[idx]} text-xl`} />
                    </div>
                    <h3
                      className="text-base font-medium text-foreground-900 mb-1"
                      style={{ fontFamily: 'var(--font-heading)' }}
                    >
                      {t(`freeAngel.features.${f.key}.name`)}
                    </h3>
                    <p className="text-xs text-foreground-400 mb-1">{t(`freeAngel.features.${f.key}.nameEn`)}</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(`freeAngel.features.${f.key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Three Guided Sessions ────────────────────────── */}
        <section className="bg-background-100/50 py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2
              className="text-2xl md:text-3xl text-center mb-2 text-foreground-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              {t('freeAngel.sessions.title')}
            </h2>
            <p className="text-sm text-foreground-400 text-center mb-2">{t('freeAngel.sessions.titleEn')}</p>
            <p className="text-sm text-foreground-400 text-center max-w-xl mx-auto mb-10">
              {t('freeAngel.sessions.subtitle')}
            </p>

            <div className="flex flex-col gap-4">
              {sessions.map((s) => {
                const isActive = completedSessions >= s.step - 1;
                const isDone = completedSessions >= s.step;
                return (
                  <div
                    key={s.key}
                    className={`rounded-lg border p-5 md:p-6 flex flex-col md:flex-row md:items-start gap-4 transition-all ${
                      isDone
                        ? 'bg-accent-50/40 border-accent-200/50'
                        : isActive
                        ? 'bg-background-50 border-foreground-200/60'
                        : 'bg-background-50 border-foreground-100/40 opacity-50'
                    }`}
                  >
                    <div className="flex items-center gap-3 shrink-0">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                          isDone
                            ? 'bg-accent-500 text-background-50'
                            : isActive
                            ? 'bg-foreground-200 text-foreground-700'
                            : 'bg-foreground-100 text-foreground-400'
                        }`}
                      >
                        {isDone ? (
                          <i className="ri-check-line" />
                        ) : (
                          s.step
                        )}
                      </div>
                      <div className="md:hidden">
                        <h3 className="text-base font-medium text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                          {t(`freeAngel.sessions.${s.key}.title`)}
                        </h3>
                        <p className="text-xs text-foreground-400">{t(`freeAngel.sessions.${s.key}.titleEn`)}</p>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="hidden md:block mb-1">
                        <h3 className="text-base font-medium text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
                          {t(`freeAngel.sessions.${s.key}.title`)}
                        </h3>
                        <p className="text-xs text-foreground-400">{t(`freeAngel.sessions.${s.key}.titleEn`)}</p>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                        {t(`freeAngel.sessions.${s.key}.desc`)}
                      </p>
                      {isActive && !isDone && (
                        <button
                          onClick={() => handleSessionComplete(s.step)}
                          className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer"
                        >
                          {s.step === 1 ? '開始第一次旅程' : s.step === 2 ? '開始第二次旅程' : '開始第三次旅程'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── After Three Sessions ──────────────────────── */}
        <section className="max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center">
          <div
            className={`rounded-lg border p-8 md:p-10 transition-all ${
              allSessionsDone
                ? 'bg-accent-50/30 border-accent-300/60'
                : 'bg-background-100/30 border-foreground-100/40'
            }`}
          >
            <AngelIcon variant={allSessionsDone ? 'gold' : 'default'} size="md" />
            <p className={`mt-4 text-base md:text-lg leading-relaxed mb-6 ${allSessionsDone ? 'text-foreground-900' : 'text-foreground-400'}`}>
              {t('freeAngel.sessions.afterThree')}
            </p>
            {allSessionsDone ? (
              <Link
                to="/living-profile"
                className="whitespace-nowrap inline-flex px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer"
              >
                {t('freeAngel.sessions.createProfile')}
              </Link>
            ) : (
              <p className="text-sm text-foreground-400 italic">
                完成三次旅程後，這個按鈕將會亮起。
              </p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}